import { action, makeObservable, observable } from 'mobx';

export abstract class BaseSelection<T> {
  @observable
  selectedItems = new Set<string>();

  @observable
  isSelecting = false;

  @action
  toggleSelection(itemId: string) {
    this.setIsSelecting(true);
    if (this.selectedItems.has(itemId)) {
      this.selectedItems.delete(itemId);
    } else {
      this.selectedItems.add(itemId);
    }
  }

  @action
  clearSelection() {
    this.setIsSelecting(false);
    if (this.selectedItems) {
      this.selectedItems.clear();
    }
  }

  @action
  selectAll() {
    this.selectedItems.clear();
    const items = this.getAvailableItems();
    if (items) {
      for (let i = 0; i < items.length; i++) {
        this.selectedItems.add(this.getItemId(items[i]));
      }
    }
    this.setIsSelecting(true);
  }

  @action
  deselectAll() {
    this.selectedItems.clear();
  }

  @action
  selectItems(itemIds: string[]) {
    this.setIsSelecting(true);
    itemIds.forEach((id) => this.selectedItems.add(id));
  }

  @action
  deselectItems(itemIds: string[]) {
    itemIds.forEach((id) => this.selectedItems.delete(id));
  }

  @action
  setIsSelecting(isSelecting: boolean) {
    this.isSelecting = isSelecting;
  }

  @action
  stopSelecting() {
    this.deselectAll();
    this.setIsSelecting(false);
  }

  @action
  setSelection(itemIds: string[]) {
    this.selectedItems.clear();
    this.selectItems(itemIds);
  }

  get selectedCount(): number {
    return this.selectedItems.size;
  }

  get hasSelection(): boolean {
    return this.selectedItems.size > 0;
  }

  get hasMultipleSelection(): boolean {
    return this.selectedItems.size > 1;
  }

  isSelected(itemId: string): boolean {
    return this.selectedItems.has(itemId);
  }

  getSelectedItems(): string[] {
    return Array.from(this.selectedItems);
  }

  constructor() {
    makeObservable(this);
  }

  // Abstract method that subclasses must implement to provide available items
  protected abstract getAvailableItems(): T[];

  // Abstract method that subclasses must implement to get the ID of an item
  protected abstract getItemId(item: T): string;
}
